export type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[]

export type Database = {
  // Allows to automatically instantiate createClient with right options
  // instead of createClient<Database, { PostgrestVersion: 'XX' }>(URL, KEY)
  __InternalSupabase: {
    PostgrestVersion: '12.2.3 (519615d)'
  }
  public: {
    Tables: {
      actions: {
        Row: {
          created_at: string
          id: number
          model: string
          new_value: number
          old_value: number
          type: Database['public']['Enums']['action_types']
        }
        Insert: {
          created_at?: string
          id?: number
          model: string
          new_value: number
          old_value: number
          type: Database['public']['Enums']['action_types']
        }
        Update: {
          created_at?: string
          id?: number
          model?: string
          new_value?: number
          old_value?: number
          type?: Database['public']['Enums']['action_types']
        }
        Relationships: [
          {
            foreignKeyName: 'price_actions_model_fkey'
            columns: ['model']
            isOneToOne: false
            referencedRelation: 'models_v2'
            referencedColumns: ['stock_code']
          },
        ]
      }
      age_groups: {
        Row: {
          created_at: string
          id: number
          key: string
          name: string
        }
        Insert: {
          created_at?: string
          id?: number
          key: string
          name: string
        }
        Update: {
          created_at?: string
          id?: number
          key?: string
          name?: string
        }
        Relationships: []
      }
      agent_chats: {
        Row: {
          chat_id: string
          created_at: string
          id: string
          messages: Json
          summary: string | null
          total_input_tokens: number
          total_output_tokens: number
          total_steps: number
          updated_at: string
          user_id: string
        }
        Insert: {
          chat_id: string
          created_at?: string
          id?: string
          messages?: Json
          summary?: string | null
          total_input_tokens?: number
          total_output_tokens?: number
          total_steps?: number
          updated_at?: string
          user_id: string
        }
        Update: {
          chat_id?: string
          created_at?: string
          id?: string
          messages?: Json
          summary?: string | null
          total_input_tokens?: number
          total_output_tokens?: number
          total_steps?: number
          updated_at?: string
          user_id?: string
        }
        Relationships: []
      }
      agent_usage_logs: {
        Row: {
          cache_creation_tokens: number
          cache_read_tokens: number
          chat_id: string
          created_at: string
          id: string
          input_tokens: number
          output_tokens: number
          user_id: string
        }
        Insert: {
          cache_creation_tokens?: number
          cache_read_tokens?: number
          chat_id: string
          created_at?: string
          id?: string
          input_tokens?: number
          output_tokens?: number
          user_id: string
        }
        Update: {
          cache_creation_tokens?: number
          cache_read_tokens?: number
          chat_id?: string
          created_at?: string
          id?: string
          input_tokens?: number
          output_tokens?: number
          user_id?: string
        }
        Relationships: []
      }
      brand_error_logs: {
        Row: {
          created_at: string
          data: Json
          error: string
          function: string
          id: number
          message: string
        }
        Insert: {
          created_at?: string
          data: Json
          error: string
          function: string
          id?: number
          message: string
        }
        Update: {
          created_at?: string
          data?: Json
          error?: string
          function?: string
          id?: number
          message?: string
        }
        Relationships: []
      }
      customer_requests: {
        Row: {
          created_at: string
          customer: number
          id: number
          note: string | null
          products: string[] | null
          request_type: Database['public']['Enums']['customer_request_type'] | null
          status: Database['public']['Enums']['customer_request_status']
        }
        Insert: {
          created_at?: string
          customer: number
          id?: number
          note?: string | null
          products?: string[] | null
          request_type?: Database['public']['Enums']['customer_request_type'] | null
          status?: Database['public']['Enums']['customer_request_status']
        }
        Update: {
          created_at?: string
          customer?: number
          id?: number
          note?: string | null
          products?: string[] | null
          request_type?: Database['public']['Enums']['customer_request_type'] | null
          status?: Database['public']['Enums']['customer_request_status']
        }
        Relationships: [
          {
            foreignKeyName: 'customer_requests_customer_fkey'
            columns: ['customer']
            isOneToOne: false
            referencedRelation: 'customers'
            referencedColumns: ['id']
          },
        ]
      }
      customers: {
        Row: {
          address: string | null
          can_promote: boolean
          country: string | null
          created_at: string
          email: string | null
          full_name: string
          id: number
          phone: string | null
        }
        Insert: {
          address?: string | null
          can_promote?: boolean
          country?: string | null
          created_at?: string
          email?: string | null
          full_name: string
          id?: number
          phone?: string | null
        }
        Update: {
          address?: string | null
          can_promote?: boolean
          country?: string | null
          created_at?: string
          email?: string | null
          full_name?: string
          id?: number
          phone?: string | null
        }
        Relationships: []
      }
      employees: {
        Row: {
          created_at: string
          full_name: string
          id: number
          position: string
        }
        Insert: {
          created_at?: string
          full_name: string
          id?: number
          position: string
        }
        Update: {
          created_at?: string
          full_name?: string
          id?: number
          position?: string
        }
        Relationships: []
      }
      google_reviews: {
        Row: {
          author_name: string
          created_at: string
          id: number
          rating: number
          text: string
        }
        Insert: {
          author_name: string
          created_at?: string
          id?: number
          rating: number
          text: string
        }
        Update: {
          author_name?: string
          created_at?: string
          id?: number
          rating?: number
          text?: string
        }
        Relationships: []
      }
      inventory: {
        Row: {
          created_at: string
          id: number
          import_file_name: string | null
          location: Database['public']['Enums']['stores']
          sales: number | null
          status: Database['public']['Enums']['inventory_status']
          stock_code: string
        }
        Insert: {
          created_at?: string
          id?: number
          import_file_name?: string | null
          location: Database['public']['Enums']['stores']
          sales?: number | null
          status?: Database['public']['Enums']['inventory_status']
          stock_code: string
        }
        Update: {
          created_at?: string
          id?: number
          import_file_name?: string | null
          location?: Database['public']['Enums']['stores']
          sales?: number | null
          status?: Database['public']['Enums']['inventory_status']
          stock_code?: string
        }
        Relationships: [
          {
            foreignKeyName: 'inventory_sales_fkey'
            columns: ['sales']
            isOneToOne: false
            referencedRelation: 'visitors'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'inventory_stock_code_fkey'
            columns: ['stock_code']
            isOneToOne: false
            referencedRelation: 'models'
            referencedColumns: ['stock_code']
          },
        ]
      }
      inventory_v2: {
        Row: {
          cost: number
          created_at: string
          id: number
          import_file_name: string | null
          inventory_item_id: string | null
          location: Database['public']['Enums']['stores']
          product_variant_id: string | null
          sales: number | null
          size: number
          status: Database['public']['Enums']['inventory_status']
          stock_code: string | null
          stock_code_v2: string
        }
        Insert: {
          cost: number
          created_at?: string
          id?: number
          import_file_name?: string | null
          inventory_item_id?: string | null
          location: Database['public']['Enums']['stores']
          product_variant_id?: string | null
          sales?: number | null
          size?: number
          status?: Database['public']['Enums']['inventory_status']
          stock_code?: string | null
          stock_code_v2: string
        }
        Update: {
          cost?: number
          created_at?: string
          id?: number
          import_file_name?: string | null
          inventory_item_id?: string | null
          location?: Database['public']['Enums']['stores']
          product_variant_id?: string | null
          sales?: number | null
          size?: number
          status?: Database['public']['Enums']['inventory_status']
          stock_code?: string | null
          stock_code_v2?: string
        }
        Relationships: [
          {
            foreignKeyName: 'inventory_v2_stock_code_v2_fkey'
            columns: ['stock_code_v2']
            isOneToOne: false
            referencedRelation: 'models_v2'
            referencedColumns: ['stock_code']
          },
          {
            foreignKeyName: 'inventory_v3_sales_fkey'
            columns: ['sales']
            isOneToOne: false
            referencedRelation: 'visitors'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'inventory_v3_stock_code_fkey'
            columns: ['stock_code']
            isOneToOne: false
            referencedRelation: 'models'
            referencedColumns: ['stock_code']
          },
        ]
      }
      models: {
        Row: {
          cost: number | null
          created_at: string
          diamond_ct: number | null
          garnet_ct: number | null
          id: number
          image_urls: string[] | null
          material: Database['public']['Enums']['material']
          price: number
          product_type: Database['public']['Enums']['product_types']
          set_id: number
          size: number | null
          status: Database['public']['Enums']['model_status']
          stock_code: string
          supplier: number
          supplier_code: string
          weight: number
        }
        Insert: {
          cost?: number | null
          created_at?: string
          diamond_ct?: number | null
          garnet_ct?: number | null
          id?: number
          image_urls?: string[] | null
          material: Database['public']['Enums']['material']
          price: number
          product_type: Database['public']['Enums']['product_types']
          set_id: number
          size?: number | null
          status?: Database['public']['Enums']['model_status']
          stock_code: string
          supplier: number
          supplier_code: string
          weight: number
        }
        Update: {
          cost?: number | null
          created_at?: string
          diamond_ct?: number | null
          garnet_ct?: number | null
          id?: number
          image_urls?: string[] | null
          material?: Database['public']['Enums']['material']
          price?: number
          product_type?: Database['public']['Enums']['product_types']
          set_id?: number
          size?: number | null
          status?: Database['public']['Enums']['model_status']
          stock_code?: string
          supplier?: number
          supplier_code?: string
          weight?: number
        }
        Relationships: [
          {
            foreignKeyName: 'models_supplier_fkey'
            columns: ['supplier']
            isOneToOne: false
            referencedRelation: 'suppliers'
            referencedColumns: ['id']
          },
        ]
      }
      models_v2: {
        Row: {
          created_at: string
          diamond_ct: number | null
          garnet_ct: number | null
          id: number
          image_urls: string[] | null
          material: Database['public']['Enums']['material']
          price: number
          product_type: Database['public']['Enums']['product_types']
          set_id: number
          shopify_product_id: string | null
          status: Database['public']['Enums']['model_status']
          status_v2: Database['public']['Enums']['model_status_v2']
          stock_code: string
          supplier: number
          supplier_code: string
          updated_at: string | null
          weight: number
        }
        Insert: {
          created_at?: string
          diamond_ct?: number | null
          garnet_ct?: number | null
          id?: number
          image_urls?: string[] | null
          material: Database['public']['Enums']['material']
          price: number
          product_type: Database['public']['Enums']['product_types']
          set_id: number
          shopify_product_id?: string | null
          status?: Database['public']['Enums']['model_status']
          status_v2?: Database['public']['Enums']['model_status_v2']
          stock_code: string
          supplier: number
          supplier_code: string
          updated_at?: string | null
          weight: number
        }
        Update: {
          created_at?: string
          diamond_ct?: number | null
          garnet_ct?: number | null
          id?: number
          image_urls?: string[] | null
          material?: Database['public']['Enums']['material']
          price?: number
          product_type?: Database['public']['Enums']['product_types']
          set_id?: number
          shopify_product_id?: string | null
          status?: Database['public']['Enums']['model_status']
          status_v2?: Database['public']['Enums']['model_status_v2']
          stock_code?: string
          supplier?: number
          supplier_code?: string
          updated_at?: string | null
          weight?: number
        }
        Relationships: [
          {
            foreignKeyName: 'models_v3_supplier_fkey'
            columns: ['supplier']
            isOneToOne: false
            referencedRelation: 'suppliers'
            referencedColumns: ['id']
          },
        ]
      }
      orders: {
        Row: {
          created_at: string
          id: number
          order_id: string
          status: Database['public']['Enums']['order_status']
        }
        Insert: {
          created_at?: string
          id?: number
          order_id: string
          status: Database['public']['Enums']['order_status']
        }
        Update: {
          created_at?: string
          id?: number
          order_id?: string
          status?: Database['public']['Enums']['order_status']
        }
        Relationships: []
      }
      profiles: {
        Row: {
          created_at: string
          email: string
          full_name: string | null
          id: number
          role: string
          user_id: string
        }
        Insert: {
          created_at?: string
          email: string
          full_name?: string | null
          id?: number
          role: string
          user_id: string
        }
        Update: {
          created_at?: string
          email?: string
          full_name?: string | null
          id?: number
          role?: string
          user_id?: string
        }
        Relationships: []
      }
      returns: {
        Row: {
          created_at: string
          id: number
          replacement_good: number | null
          returned_good: number
          sales: number
        }
        Insert: {
          created_at?: string
          id?: number
          replacement_good?: number | null
          returned_good: number
          sales: number
        }
        Update: {
          created_at?: string
          id?: number
          replacement_good?: number | null
          returned_good?: number
          sales?: number
        }
        Relationships: [
          {
            foreignKeyName: 'returns_replacement_good_fkey'
            columns: ['replacement_good']
            isOneToOne: false
            referencedRelation: 'inventory_v2'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'returns_returned_good_fkey'
            columns: ['returned_good']
            isOneToOne: false
            referencedRelation: 'inventory_v2'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'returns_sales_fkey'
            columns: ['sales']
            isOneToOne: false
            referencedRelation: 'visitors'
            referencedColumns: ['id']
          },
        ]
      }
      sales_agencies: {
        Row: {
          agency_id: number
          agency_name: string
          commission: number
          country: string | null
          created_at: string
          description: string
          email: string | null
          id: number
          min_commission: number
          should_notify: boolean
        }
        Insert: {
          agency_id?: number
          agency_name: string
          commission?: number
          country?: string | null
          created_at?: string
          description?: string
          email?: string | null
          id?: number
          min_commission?: number
          should_notify?: boolean
        }
        Update: {
          agency_id?: number
          agency_name?: string
          commission?: number
          country?: string | null
          created_at?: string
          description?: string
          email?: string | null
          id?: number
          min_commission?: number
          should_notify?: boolean
        }
        Relationships: []
      }
      shared_carts: {
        Row: {
          created_at: string
          id: string
          items: Json[]
        }
        Insert: {
          created_at?: string
          id?: string
          items: Json[]
        }
        Update: {
          created_at?: string
          id?: string
          items?: Json[]
        }
        Relationships: []
      }
      shopify_sales: {
        Row: {
          created_at: string
          id: number
          shopify_order_id: number
          visitor: number
        }
        Insert: {
          created_at?: string
          id?: number
          shopify_order_id: number
          visitor: number
        }
        Update: {
          created_at?: string
          id?: number
          shopify_order_id?: number
          visitor?: number
        }
        Relationships: [
          {
            foreignKeyName: 'shopify_sales_shopify_order_id_fkey'
            columns: ['shopify_order_id']
            isOneToOne: false
            referencedRelation: 'orders'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'shopify_sales_visitor_fkey'
            columns: ['visitor']
            isOneToOne: false
            referencedRelation: 'visitors'
            referencedColumns: ['id']
          },
        ]
      }
      shops: {
        Row: {
          created_at: string
          google_rating: number | null
          id: number
          name: string
          under_maintenance: boolean
          user_ratings_total: number | null
        }
        Insert: {
          created_at?: string
          google_rating?: number | null
          id?: number
          name: string
          under_maintenance: boolean
          user_ratings_total?: number | null
        }
        Update: {
          created_at?: string
          google_rating?: number | null
          id?: number
          name?: string
          under_maintenance?: boolean
          user_ratings_total?: number | null
        }
        Relationships: []
      }
      suppliers: {
        Row: {
          created_at: string
          email: string | null
          full_name: string | null
          id: number
        }
        Insert: {
          created_at?: string
          email?: string | null
          full_name?: string | null
          id?: number
        }
        Update: {
          created_at?: string
          email?: string | null
          full_name?: string | null
          id?: number
        }
        Relationships: []
      }
      user_roles: {
        Row: {
          id: number
          role: Database['public']['Enums']['app_role']
          user_id: string
        }
        Insert: {
          id?: number
          role: Database['public']['Enums']['app_role']
          user_id: string
        }
        Update: {
          id?: number
          role?: Database['public']['Enums']['app_role']
          user_id?: string
        }
        Relationships: []
      }
      visitors: {
        Row: {
          age_group: number | null
          agency: string
          country: string
          created_at: string
          employee: string
          has_refund: boolean
          id: number
          info: string | null
          message_id: number | null
          pax: number
          payment_type: string | null
          product_ids: string[] | null
          products: string[] | null
          store: Database['public']['Enums']['stores']
          total: number
        }
        Insert: {
          age_group?: number | null
          agency: string
          country: string
          created_at?: string
          employee: string
          has_refund?: boolean
          id?: number
          info?: string | null
          message_id?: number | null
          pax: number
          payment_type?: string | null
          product_ids?: string[] | null
          products?: string[] | null
          store?: Database['public']['Enums']['stores']
          total: number
        }
        Update: {
          age_group?: number | null
          agency?: string
          country?: string
          created_at?: string
          employee?: string
          has_refund?: boolean
          id?: number
          info?: string | null
          message_id?: number | null
          pax?: number
          payment_type?: string | null
          product_ids?: string[] | null
          products?: string[] | null
          store?: Database['public']['Enums']['stores']
          total?: number
        }
        Relationships: [
          {
            foreignKeyName: 'visitors_age_group_fkey'
            columns: ['age_group']
            isOneToOne: false
            referencedRelation: 'age_groups'
            referencedColumns: ['id']
          },
        ]
      }
    }
    Views: {
      all_photos: {
        Row: {
          bucket_id: string | null
          id: string | null
          metadata: Json | null
          name: string | null
          owner: string | null
        }
        Insert: {
          bucket_id?: string | null
          id?: string | null
          metadata?: Json | null
          name?: string | null
          owner?: string | null
        }
        Update: {
          bucket_id?: string | null
          id?: string | null
          metadata?: Json | null
          name?: string | null
          owner?: string | null
        }
        Relationships: []
      }
    }
    Functions: {
      custom_access_token_hook: { Args: { event: Json }; Returns: Json }
      extract_set_id: { Args: never; Returns: undefined }
      get_models_with_inventory: {
        Args: { hideoutofstock?: boolean }
        Returns: {
          diamond_ct: number
          garnet_ct: number
          id: number
          image_urls: string[]
          material: Database['public']['Enums']['material']
          price: number
          product_type: Database['public']['Enums']['product_types']
          set_id: number
          shopify_product_id: string
          status_v2: Database['public']['Enums']['model_status_v2']
          stock_code: string
          supplier_code: string
          variants: Database['public']['CompositeTypes']['variant_type'][]
          weight: number
        }[]
      }
      get_random_models: {
        Args: { excluded_id: number; limit_input: number }
        Returns: {
          cost: number | null
          created_at: string
          diamond_ct: number | null
          garnet_ct: number | null
          id: number
          image_urls: string[] | null
          material: Database['public']['Enums']['material']
          price: number
          product_type: Database['public']['Enums']['product_types']
          set_id: number
          size: number | null
          status: Database['public']['Enums']['model_status']
          stock_code: string
          supplier: number
          supplier_code: string
          weight: number
        }[]
        SetofOptions: {
          from: '*'
          to: 'models'
          isOneToOne: false
          isSetofReturn: true
        }
      }
      get_random_models_v2: {
        Args: { excluded_id: number; limit_input: number }
        Returns: {
          created_at: string
          diamond_ct: number | null
          garnet_ct: number | null
          id: number
          image_urls: string[] | null
          material: Database['public']['Enums']['material']
          price: number
          product_type: Database['public']['Enums']['product_types']
          set_id: number
          shopify_product_id: string | null
          status: Database['public']['Enums']['model_status']
          status_v2: Database['public']['Enums']['model_status_v2']
          stock_code: string
          supplier: number
          supplier_code: string
          updated_at: string | null
          weight: number
        }[]
        SetofOptions: {
          from: '*'
          to: 'models_v2'
          isOneToOne: false
          isSetofReturn: true
        }
      }
      rename_vitrin_agency: { Args: never; Returns: undefined }
      search_visitors: {
        Args: { searchvalue: string }
        Returns: {
          age_group: Database['public']['Tables']['age_groups']['Row']
          agency: string
          country: string
          created_at: string
          employee: string
          has_refund: boolean
          id: number
          info: string
          message_id: number
          pax: number
          payment_type: string
          product_ids: string[]
          products: string[]
          store: Database['public']['Enums']['stores']
          total: number
        }[]
      }
      select_short_stock_codes: {
        Args: never
        Returns: {
          cost: number
          created_at: string
          diamond_ct: number
          garnet_ct: number
          id: number
          image_urls: string[]
          material: Database['public']['Enums']['material']
          organisation: number
          price: number
          product_type: Database['public']['Enums']['product_types']
          quantity: number
          set_id: number
          size: number
          status: Database['public']['Enums']['model_status']
          stock_code: string
          supplier: number
          supplier_code: string
          unique_id: string
          weight: number
        }[]
      }
      set_model_images: { Args: never; Returns: undefined }
      update_model_file_names: { Args: never; Returns: undefined }
    }
    Enums: {
      action_types: 'price_change'
      app_permission:
        | 'models.write'
        | 'inventory.write'
        | 'sales.write'
        | 'agencies.write'
        | 'returns.write'
        | 'customers.write'
        | 'suupliers.write'
      app_role: 'admin' | 'moderator' | 'marketing'
      customer_request_status: 'pending' | 'complete' | 'ready' | 'failed'
      customer_request_type: 'resize' | 'repair' | 'order' | 'wish' | 'waitlist'
      inventory_status: 'sold' | 'available' | 'unavailable'
      material:
        | 'rose_diamond'
        | 'rose_gold'
        | 'white_silver'
        | 'white_diamond'
        | 'yellow_diamond'
        | 'moldavite_gemstone'
        | 'garnet_gemstone'
      model_status:
        | 'ready'
        | 'incomplete'
        | 'deprecated'
        | 'missing_images'
        | 'in_eshop'
        | 'in_stripe'
      model_status_v2: 'deprecated' | 'incomplete' | 'ready' | 'in_eshop'
      order_status: 'to_review' | 'confirmed' | 'shipped' | 'finalized'
      product_types: 'necklace' | 'ring' | 'earring' | 'pendant' | 'bracelet' | 'gemstone' | 'chain'
      stores: 'wollem' | 'mandarin'
    }
    CompositeTypes: {
      variant_type: {
        product_variant_id: string | null
        inventory_item_id: string | null
        inventory_quantity: number | null
        size: number | null
      }
    }
  }
}

type DatabaseWithoutInternals = Omit<Database, '__InternalSupabase'>

type DefaultSchema = DatabaseWithoutInternals[Extract<keyof Database, 'public'>]

export type Tables<
  DefaultSchemaTableNameOrOptions extends
    | keyof (DefaultSchema['Tables'] & DefaultSchema['Views'])
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'] &
        DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Views'])
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? (DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'] &
      DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Views'])[TableName] extends {
      Row: infer R
    }
    ? R
    : never
  : DefaultSchemaTableNameOrOptions extends keyof (DefaultSchema['Tables'] & DefaultSchema['Views'])
    ? (DefaultSchema['Tables'] & DefaultSchema['Views'])[DefaultSchemaTableNameOrOptions] extends {
        Row: infer R
      }
      ? R
      : never
    : never

export type TablesInsert<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema['Tables']
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables']
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'][TableName] extends {
      Insert: infer I
    }
    ? I
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema['Tables']
    ? DefaultSchema['Tables'][DefaultSchemaTableNameOrOptions] extends {
        Insert: infer I
      }
      ? I
      : never
    : never

export type TablesUpdate<
  DefaultSchemaTableNameOrOptions extends
    | keyof DefaultSchema['Tables']
    | { schema: keyof DatabaseWithoutInternals },
  TableName extends DefaultSchemaTableNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables']
    : never = never,
> = DefaultSchemaTableNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaTableNameOrOptions['schema']]['Tables'][TableName] extends {
      Update: infer U
    }
    ? U
    : never
  : DefaultSchemaTableNameOrOptions extends keyof DefaultSchema['Tables']
    ? DefaultSchema['Tables'][DefaultSchemaTableNameOrOptions] extends {
        Update: infer U
      }
      ? U
      : never
    : never

export type Enums<
  DefaultSchemaEnumNameOrOptions extends
    | keyof DefaultSchema['Enums']
    | { schema: keyof DatabaseWithoutInternals },
  EnumName extends DefaultSchemaEnumNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions['schema']]['Enums']
    : never = never,
> = DefaultSchemaEnumNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[DefaultSchemaEnumNameOrOptions['schema']]['Enums'][EnumName]
  : DefaultSchemaEnumNameOrOptions extends keyof DefaultSchema['Enums']
    ? DefaultSchema['Enums'][DefaultSchemaEnumNameOrOptions]
    : never

export type CompositeTypes<
  PublicCompositeTypeNameOrOptions extends
    | keyof DefaultSchema['CompositeTypes']
    | { schema: keyof DatabaseWithoutInternals },
  CompositeTypeName extends PublicCompositeTypeNameOrOptions extends {
    schema: keyof DatabaseWithoutInternals
  }
    ? keyof DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions['schema']]['CompositeTypes']
    : never = never,
> = PublicCompositeTypeNameOrOptions extends {
  schema: keyof DatabaseWithoutInternals
}
  ? DatabaseWithoutInternals[PublicCompositeTypeNameOrOptions['schema']]['CompositeTypes'][CompositeTypeName]
  : PublicCompositeTypeNameOrOptions extends keyof DefaultSchema['CompositeTypes']
    ? DefaultSchema['CompositeTypes'][PublicCompositeTypeNameOrOptions]
    : never

export const Constants = {
  public: {
    Enums: {
      action_types: ['price_change'],
      app_permission: [
        'models.write',
        'inventory.write',
        'sales.write',
        'agencies.write',
        'returns.write',
        'customers.write',
        'suupliers.write',
      ],
      app_role: ['admin', 'moderator', 'marketing'],
      customer_request_status: ['pending', 'complete', 'ready', 'failed'],
      customer_request_type: ['resize', 'repair', 'order', 'wish', 'waitlist'],
      inventory_status: ['sold', 'available', 'unavailable'],
      material: [
        'rose_diamond',
        'rose_gold',
        'white_silver',
        'white_diamond',
        'yellow_diamond',
        'moldavite_gemstone',
        'garnet_gemstone',
      ],
      model_status: [
        'ready',
        'incomplete',
        'deprecated',
        'missing_images',
        'in_eshop',
        'in_stripe',
      ],
      model_status_v2: ['deprecated', 'incomplete', 'ready', 'in_eshop'],
      order_status: ['to_review', 'confirmed', 'shipped', 'finalized'],
      product_types: ['necklace', 'ring', 'earring', 'pendant', 'bracelet', 'gemstone', 'chain'],
      stores: ['wollem', 'mandarin'],
    },
  },
} as const
